import type {
  DataDrivenPropertyValueSpecification,
  ExpressionSpecification,
  FeatureIdentifier,
  Map,
  MapMouseEvent,
} from "maplibre-gl";
import { isFeatureSelectable } from "../../../lib/geo/maplibreFeature.ts";
import { isSemanticSourceLayer } from "../../../lib/geo/layers.ts";

type PaintPropertyValue = DataDrivenPropertyValueSpecification<string>;
type ColorExpression = string | ExpressionSpecification;

export function attachMapHoverPresentation(
  map: Map,
): {
  destroy: () => void;
} {
  let lastCursor = "";
  let hoveredFeature: FeatureIdentifier | undefined;

  function handleMouseMove(e: MapMouseEvent): void {
    const features = map.queryRenderedFeatures(e.point);

    const poiFeature = features.find(
      (f) => isFeatureSelectable(f) && f.id !== undefined,
    );

    const newId = poiFeature?.id;
    const newSourceLayer = poiFeature?.sourceLayer;
    if (
      newId !== hoveredFeature?.id ||
      newSourceLayer !== hoveredFeature?.sourceLayer
    ) {
      if (hoveredFeature) {
        map.setFeatureState(hoveredFeature, { hover: false });
      }
      if (poiFeature && newId !== undefined && poiFeature.sourceLayer) {
        const nextHoveredFeature = {
          id: newId,
          source: poiFeature.source,
          sourceLayer: poiFeature.sourceLayer,
        };
        hoveredFeature = nextHoveredFeature;
        map.setFeatureState(nextHoveredFeature, { hover: true });
      } else {
        hoveredFeature = undefined;
      }
    }

    setCursor(features.some(isFeatureSelectable) ? "pointer" : "");
  }

  // Applies feature-state-driven paint/layout overrides on POI symbol layers.
  // Must be triggered by style.load, NOT styledata. setPaintProperty marks the
  // style dirty, which causes MapLibre to fire styledata on every subsequent
  // render frame — an infinite loop. style.load fires only when a new style
  // JSON is fully parsed (initial load or setStyle()), never from paint changes.
  function applyFeatureStateStyles(): void {
    hoveredFeature = undefined;
    const layers = map.getStyle()?.layers ?? [];
    for (const layer of layers) {
      if (layer.type !== "symbol") continue;
      const sourceLayer = (layer as { "source-layer"?: string })[
        "source-layer"
      ];
      if (!sourceLayer || !isSemanticSourceLayer(sourceLayer)) continue;

      const paint = layer.paint;
      const hoverColor = "#2563EB";

      const textColor = caseFallback(paint?.["text-color"], "rgba(0,0,0,1)");
      if (textColor !== null) {
        map.setPaintProperty(layer.id, "text-color", [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          hoverColor,
          textColor,
        ]);
      }

      // icon-color works for SDF icons; it's a no-op for raster sprites.
      const iconColor = caseFallback(paint?.["icon-color"], "rgba(0,0,0,1)");
      if (iconColor !== null) {
        map.setPaintProperty(layer.id, "icon-color", [
          "case",
          ["boolean", ["feature-state", "hover"], false],
          hoverColor,
          iconColor,
        ]);
      }
    }
  }

  function setCursor(cursor: string): void {
    if (cursor === lastCursor) return;
    map.getCanvas().style.cursor = cursor;
    lastCursor = cursor;
  }

  function destroy(): void {
    map.off("mousemove", handleMouseMove);
    map.off("style.load", applyFeatureStateStyles);
    if (hoveredFeature) {
      map.setFeatureState(hoveredFeature, { hover: false });
      hoveredFeature = undefined;
    }
    setCursor("");
  }

  map.on("mousemove", handleMouseMove);
  map.on("style.load", applyFeatureStateStyles);

  return { destroy };
}

// Returns `value` (or `defaultValue` for null/undefined) when safe to use as
// the fallback branch of a MapLibre ["case", ...] expression. Returns null when
// the value must be skipped: bare objects (MapLibre's internal color repr) and
// top-level zoom expressions can't be nested inside case.
function caseFallback(
  value: PaintPropertyValue | undefined,
  defaultValue: string,
): ColorExpression | null {
  if (value === null || value === undefined) return defaultValue;
  if (typeof value === "string") return value;
  if (!isExpressionSpecification(value)) return null;
  const expr = value;
  // ["interpolate", interpolationType, ["zoom"], ...] — zoom at index 2
  if (
    expr[0] === "interpolate" && Array.isArray(expr[2]) &&
    expr[2][0] === "zoom"
  ) return null;
  // ["step", ["zoom"], ...] — zoom at index 1
  if (expr[0] === "step" && Array.isArray(expr[1]) && expr[1][0] === "zoom") {
    return null;
  }
  return value;
}

function isExpressionSpecification(
  value: PaintPropertyValue,
): value is ExpressionSpecification {
  return Array.isArray(value) && typeof value[0] === "string";
}
